import { useState } from "react"
import { useTranslation } from "react-i18next"
import dayjs from "dayjs"
import { Calendar } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

interface DateRangePickerProps {
  startDate: string
  endDate: string
  onChange: (startDate: string, endDate: string) => void
}

const MAX_DAYS = 31

export const DateRangePicker = ({ startDate, endDate, onChange }: DateRangePickerProps) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [error, setError] = useState("")

  const validateDateRange = (start: string, end: string): boolean => {
    if (!start || !end) return true
    return dayjs(end).diff(dayjs(start), "day") + 1 <= MAX_DAYS
  }

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStartDate = e.target.value
    setError("")
    if (!newStartDate) return

    if (dayjs(newStartDate).isAfter(dayjs(endDate))) {
      onChange(newStartDate, newStartDate)
      return
    }

    if (validateDateRange(newStartDate, endDate)) {
      onChange(newStartDate, endDate)
    } else {
      const maxEnd = dayjs(newStartDate).add(MAX_DAYS - 1, "day")
      const finalEnd = maxEnd.isAfter(dayjs()) ? dayjs() : maxEnd
      onChange(newStartDate, finalEnd.format("YYYY-MM-DD"))
      setError(t("analytics.maxDateRangeExceeded", { days: MAX_DAYS }))
    }
  }

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEndDate = e.target.value
    setError("")
    if (!newEndDate) return

    if (dayjs(newEndDate).isBefore(dayjs(startDate))) {
      onChange(newEndDate, newEndDate)
      return
    }

    if (validateDateRange(startDate, newEndDate)) {
      onChange(startDate, newEndDate)
    } else {
      const maxEnd = dayjs(startDate).add(MAX_DAYS - 1, "day")
      const finalEnd = maxEnd.isAfter(dayjs()) ? dayjs() : maxEnd
      onChange(startDate, finalEnd.format("YYYY-MM-DD"))
      setError(t("analytics.maxDateRangeExceeded", { days: MAX_DAYS }))
    }
  }

  const formatDisplayDate = (date: string) => dayjs(date).format("MMM D, YYYY")

  const getDateRangeLabel = () => {
    if (startDate && endDate) {
      if (dayjs(endDate).diff(dayjs(startDate), "day") === 0) return formatDisplayDate(startDate)
      return `${formatDisplayDate(startDate)} – ${formatDisplayDate(endDate)}`
    }
    return t("analytics.selectDateRange")
  }

  const maxEndDate = (() => {
    const limit = dayjs(startDate).add(MAX_DAYS - 1, "day")
    return (limit.isAfter(dayjs()) ? dayjs() : limit).format("YYYY-MM-DD")
  })()

  return (
    <div className="relative">
      <Button
        variant="outline"
        size="sm"
        onClick={() => {
          setIsOpen(!isOpen)
          if (isOpen) setError("")
        }}
        className="gap-2"
      >
        <Calendar className="size-3.5" />
        <span>{getDateRangeLabel()}</span>
      </Button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => {
              setIsOpen(false)
              setError("")
            }}
          />
          <div className="absolute right-0 top-full z-20 mt-2 w-[300px] rounded-md border bg-popover p-4 shadow-lg">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <Label>{t("analytics.startDate")}</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={handleStartDateChange}
                  max={endDate || dayjs().format("YYYY-MM-DD")}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>{t("analytics.endDate")}</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={handleEndDateChange}
                  min={startDate}
                  max={maxEndDate}
                />
              </div>
              {error && (
                <p className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">
                  {error}
                </p>
              )}
              <Separator />
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => {
                    onChange(
                      dayjs().subtract(29, "day").format("YYYY-MM-DD"),
                      dayjs().format("YYYY-MM-DD")
                    )
                    setIsOpen(false)
                  }}
                >
                  {t("analytics.last30Days")}
                </Button>
                <Button
                  size="sm"
                  disabled={!!error}
                  onClick={() => {
                    if (!error) setIsOpen(false)
                  }}
                >
                  {t("analytics.apply")}
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

const Separator = () => <div className="h-px bg-border" />
